import { CircleAlert, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import type { ImportPlan, PlannedAction, PlannedTournament } from '../../../import/plan';

/**
 * Rows drawn at once. Only ever reached by the "all" view of a large file — the changing rows,
 * which are what this step is for, are never cut off by it.
 */
const PREVIEW_LIMIT = 60;

type View = 'changing' | 'unchanged' | 'all';

const failed = (t: PlannedTournament) => t.issues.some((i) => i.level === 'error');

/** Changing means "something will happen to this", and a row with an error is going to matter. */
const isChanging = (t: PlannedTournament) => t.action !== 'unchanged' || failed(t);

/**
 * Null is "nothing was guaranteed", which is not a guarantee of zero — so it is "None", not a
 * dash. A dash is reserved for a row the file couldn't be read from at all.
 */
const guaranteeOf = (t: PlannedTournament): string => {
  const values = t.values;
  if (!values) return '—';
  if (values.guarantee === null) return 'None';
  return values.guaranteeUnits
    ? `${values.guarantee.toLocaleString()} ${values.guaranteeUnits}`
    : `$${values.guarantee.toLocaleString()}`;
};

function ActionChip({ action }: { action: PlannedAction }) {
  if (action === 'unchanged') {
    return <span className="text-[10px] uppercase tracking-wide text-ink-800/40">Unchanged</span>;
  }
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        action === 'create' ? 'bg-felt-50 text-felt-900' : 'bg-gold-500/20 text-ink-900'
      }`}
    >
      {action === 'create' ? 'New' : 'Update'}
    </span>
  );
}

function Tally({ value, label, tone }: { value: number; label: string; tone: string }) {
  return (
    <div className="rounded-lg border border-ink-800/10 px-4 py-3">
      <div className={`num text-xl font-semibold ${tone}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-ink-800/50">{label}</div>
    </div>
  );
}

function ViewTab({
  active,
  count,
  label,
  onClick,
}: {
  active: boolean;
  count: number;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-xs transition disabled:opacity-40 ${
        active
          ? 'border-felt-700 bg-felt-700 text-white'
          : 'border-ink-800/15 text-ink-800/70 hover:border-felt-500'
      }`}
    >
      {label} <span className="num">{count}</span>
    </button>
  );
}

/**
 * What the file says, before anything is written.
 *
 * The table is the only place per-tournament detail lives: a row says what will happen to it and,
 * where something moved, exactly what moved — against the values it moved from. The panel above
 * it is for the file, not the tournaments: a column nobody could read, a row with the wrong number
 * of values. So the two never carry the same thing twice, and neither grows without bound.
 *
 * It opens on what is going to change. A re-import is mostly rows where nothing moved, and hunting
 * one shifted start time through ninety-seven unchanged ones is not review, it is proofreading.
 */
export function ReviewStep({
  plan,
  defaultTags,
}: {
  plan: ImportPlan;
  defaultTags: readonly string[];
}) {
  const changing = plan.tournaments.filter(isChanging);
  const unchanged = plan.tournaments.filter((t) => !isChanging(t));

  // Held only once the operator has an opinion, so a newly-read file opens on the right view for
  // what it actually contains rather than inheriting the last one's.
  const [chosen, setChosen] = useState<View | null>(null);
  const view: View = chosen ?? (changing.length > 0 && unchanged.length > 0 ? 'changing' : 'all');

  const rows = view === 'changing' ? changing : view === 'unchanged' ? unchanged : plan.tournaments;
  const shown = rows.slice(0, PREVIEW_LIMIT);
  const withSessions = plan.tournaments.filter((t) => t.sessionCount > 0).length;

  /*
   * Fills the dialog rather than growing past it. The issue panel takes what it needs up to a
   * share of the height and the table takes the rest, so neither pushes the other off the screen
   * and only they scroll — never the dialog behind them.
   */
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-5">
        <Tally value={plan.creates.length} label="New" tone="text-ink-950" />
        <Tally
          value={plan.updates.length}
          label="Updated"
          tone={plan.updates.length > 0 ? 'text-gold-500' : 'text-ink-800/40'}
        />
        <Tally
          value={plan.unchangedCount}
          label="Unchanged"
          tone={plan.unchangedCount > 0 ? 'text-ink-800/70' : 'text-ink-800/40'}
        />
        <Tally
          value={plan.errorCount}
          label="Errors"
          tone={plan.errorCount > 0 ? 'text-chip-500' : 'text-ink-800/40'}
        />
        <Tally
          value={plan.warningCount}
          label="Warnings"
          tone={plan.warningCount > 0 ? 'text-gold-500' : 'text-ink-800/40'}
        />
      </div>

      <p className="shrink-0 text-xs text-ink-800/60">
        {plan.tournaments.length} tournament{plan.tournaments.length === 1 ? '' : 's'} in{' '}
        {plan.dataRows} row{plan.dataRows === 1 ? '' : 's'}
        {plan.blankRows > 0 ? ` (${plan.blankRows} blank skipped)` : ''}.{' '}
        {plan.hasHeader ? 'First line read as headings.' : 'No heading row — every line is data.'}{' '}
        {plan.grouped
          ? `Lines sharing an internal ID are sittings of one event; ${withSessions} ${
              withSessions === 1 ? 'has a schedule' : 'have schedules'
            }.`
          : 'One tournament per line.'}{' '}
        Nothing is deleted — a tournament this room has that the file doesn&apos;t mention is left
        alone.
        {defaultTags.length > 0
          ? ' A blank tags cell takes this room’s default tags on a new tournament, and leaves an existing one’s tags alone.'
          : ''}
      </p>

      {plan.issues.length > 0 ? (
        <section className="flex max-h-[35%] min-h-0 flex-col overflow-hidden rounded-lg border border-ink-800/10">
          <header className="shrink-0 border-b border-ink-800/10 bg-felt-50/50 px-4 py-2">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-800/50">
              What needs attention ({plan.issues.length})
            </h4>
          </header>
          <ul className="min-h-0 flex-1 divide-y divide-ink-800/5 overflow-auto">
            {plan.issues.map((issue, index) => (
              <li key={index} className="flex items-start gap-2 px-4 py-2 text-xs">
                {issue.level === 'error' ? (
                  <TriangleAlert className="mt-px size-3.5 shrink-0 text-chip-500" />
                ) : (
                  <CircleAlert className="mt-px size-3.5 shrink-0 text-gold-500" />
                )}
                {issue.line !== undefined ? (
                  <span className="num shrink-0 text-ink-800/45">Line {issue.line}</span>
                ) : null}
                <span className="min-w-0 flex-1 text-ink-800/80">{issue.message}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {plan.errorCount > 0 ? (
        <p className="shrink-0 rounded-lg border border-chip-500/40 bg-chip-500/5 p-3 text-xs text-ink-900">
          Nothing is imported while there are errors. Fix the file — or the template&apos;s columns
          — and choose it again.
        </p>
      ) : null}

      {plan.tournaments.length > 0 ? (
        <section className="flex min-h-40 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-ink-800/10">
          <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-ink-800/10 bg-felt-50/50 px-3 py-2">
            <ViewTab
              active={view === 'changing'}
              count={changing.length}
              label="Changing"
              onClick={() => setChosen('changing')}
            />
            <ViewTab
              active={view === 'unchanged'}
              count={unchanged.length}
              label="Unchanged"
              onClick={() => setChosen('unchanged')}
            />
            <ViewTab
              active={view === 'all'}
              count={plan.tournaments.length}
              label="All"
              onClick={() => setChosen('all')}
            />
          </header>

          {/* The one scrolling region down here. `min-h-0` is what lets it shrink inside the
              column rather than pushing the dialog past its own height. */}
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-white text-[10px] uppercase tracking-wide text-ink-800/60 shadow-[0_1px_0_rgba(0,0,0,0.08)]">
                <tr>
                  <th className="w-[76px] px-3 py-2 font-medium">Action</th>
                  <th className="px-3 py-2 font-medium">Internal ID</th>
                  <th className="px-3 py-2 font-medium">Tournament</th>
                  <th className="px-3 py-2 font-medium">Starts</th>
                  <th className="px-3 py-2 font-medium">Buy-in</th>
                  <th className="px-3 py-2 font-medium">Guarantee</th>
                  <th className="px-3 py-2 font-medium">Schedule</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-800/5">
                {shown.map((t) => {
                  const values = t.values;
                  const dim = t.action === 'unchanged' && !failed(t);
                  return (
                    <tr
                      key={t.internalId}
                      className={failed(t) ? 'bg-chip-500/5' : dim ? 'text-ink-800/45' : ''}
                    >
                      <td className="w-[76px] whitespace-nowrap px-3 py-2 align-top">
                        <ActionChip action={t.action} />
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-[11px] text-ink-800/70">
                        {t.internalId}
                      </td>
                      <td className={`px-3 py-2 align-top ${dim ? '' : 'text-ink-950'}`}>
                        {t.name}
                        {/* What moved, against the row it moved on. This is the only place the
                            detail lives, so none of it is abbreviated away. */}
                        {t.changes.length > 0 ? (
                          <ul className="mt-1 space-y-0.5">
                            {t.changes.map((c) => (
                              <li key={c.label} className="text-[11px] leading-snug">
                                <span className="text-ink-800/45">{c.label}</span>{' '}
                                <span className="text-ink-800/50 line-through">{c.from}</span>{' '}
                                <span className="text-ink-800/40">→</span>{' '}
                                <span className="font-medium text-ink-950">{c.to}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </td>
                      <td className="num whitespace-nowrap px-3 py-2 align-top text-ink-800/70">
                        {values ? `${values.date} ${values.time}` : t.date || '—'}
                      </td>
                      <td className="num whitespace-nowrap px-3 py-2 align-top">
                        {values?.buyIn != null ? `$${values.buyIn.toLocaleString()}` : '—'}
                      </td>
                      <td className="num whitespace-nowrap px-3 py-2 align-top text-ink-800/70">
                        {guaranteeOf(t)}
                      </td>
                      <td className="px-3 py-2 align-top text-ink-800/70">
                        {!values ? (
                          '—'
                        ) : t.sessionCount > 0 ? (
                          `${t.sessionCount} sitting${t.sessionCount === 1 ? '' : 's'}`
                        ) : values.isMultiDay ? (
                          // The flag is stored rather than derived, so this is a real state and
                          // not a gap in the file.
                          <span className="text-ink-800/55">Multi-day, to be announced</span>
                        ) : (
                          'Single day'
                        )}
                      </td>
                    </tr>
                  );
                })}
                {shown.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-ink-800/50">
                      Nothing in this view.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <p className="shrink-0 border-t border-ink-800/10 px-3 py-2 text-[11px] text-ink-800/50">
            {shown.length < rows.length
              ? `Showing the first ${shown.length} of ${rows.length} — every one is checked, listed or not.`
              : `${rows.length} row${rows.length === 1 ? '' : 's'}${rows.length > 6 ? ' — scroll for the rest' : ''}.`}
          </p>
        </section>
      ) : null}
    </div>
  );
}
